import Button from "@components/atoms/Button";
import ContentBox from "@components/atoms/ContentBox";
import Table from "@components/atoms/Table";
import Filter from "@components/molecules/Filter";
import { PrivateRoutes } from "@constants/urls";
import { usePagination } from "@hooks/usePagination";
import { colors, typo } from "@styles/index";
import { Pagination } from "antd";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useFormik } from "formik";
import { useV2AppVersionsControllerFetchAppVersions, type V2AppVersionListResponseDto } from "@everon-it/api-client/contents-gw";
import { appVersionData, type AppVersionInitialValues } from "./Data";

type AppVersionTableListDataType = Omit<V2AppVersionListResponseDto, "createdAt">;

const AppVersionManageMent: React.FC = () => {
    // Data
    const filter = appVersionData.forms;
    const formInitialValues = appVersionData.formInitialValues;
    const tableContentsInfoData = appVersionData.tableList;

    // Props
    const MAIN_TITLE = "앱 버전 관리";
    const TABLE_TITLE = "앱 버전 목록";
    const FILTER_GIRD_COLUMN = 1;
    const tableButtonText = "앱 버전 등록";

    // Router
    const navigate = useNavigate();
    const DETAIL_PATH = PrivateRoutes.AppVersionDetail;
    const CREATE_PATH = PrivateRoutes.AppVersionCreate;

    // 페이지네이션
    const { page, pageSize, setPage, handlePageSizeChange, resetPage } = usePagination();

    // Formik (검색 필터)
    const formik = useFormik<AppVersionInitialValues>({
        initialValues: formInitialValues,
        onSubmit: () => {
            resetPage();
        },
    });

    // api-client 훅 사용
    const { data } = useV2AppVersionsControllerFetchAppVersions({
        page,
        pageSize,
        platform: formik.values.platform || undefined,
    });

    // 데이터 추출
    const listData = data?.rows ?? [];
    const totalCount = data?.count ?? 0;

    // 필터 초기화
    const handleFormReset = () => {
        formik.resetForm();
        resetPage();
    };

    // 검색 필터 변경 시 페이지 리셋
    useEffect(() => {
        resetPage();
    }, [formik.values.platform, resetPage]);

    /** 작성 페이지 이동 */
    const handleCreateClick = () => {
        navigate(CREATE_PATH);
    };

    /** 상세 페이지 이동 uuid */
    const handleDetailClick = (uuid: string) => {
        navigate(`${DETAIL_PATH}/${uuid}`);
    };

    const appVersionTableListData: AppVersionTableListDataType[] = listData.map((item: V2AppVersionListResponseDto) => {
        const { updatedAt, createdAt, ...rest } = item;
        return {
            ...rest,
            updatedAt: updatedAt ?? createdAt,
        };
    });

    return (
        <ContentsWrap>
            <MainHeaderTitle>{MAIN_TITLE}</MainHeaderTitle>
            <ContentBox>
                <Filter formik={formik} filterData={filter} girdColumn={FILTER_GIRD_COLUMN} formInitialValues={formInitialValues} handleFormReset={handleFormReset} />
            </ContentBox>
            <ContentBoxWithHeader>
                <TableHeader>
                    <TableTitle>{TABLE_TITLE}</TableTitle>
                    <HeaderButtonWrap>
                        <Button buttonSize="small" text={tableButtonText} onClick={handleCreateClick} />
                    </HeaderButtonWrap>
                </TableHeader>
                <Table<AppVersionTableListDataType> rowData={appVersionTableListData} tableContentsInfoData={tableContentsInfoData} currentPageSize={pageSize} onDetailClick={handleDetailClick} />
                <TableBottomWrap>
                    <ContentsTotal>
                        총<span>{totalCount}</span>
                    </ContentsTotal>
                    <PageNationWrap>
                        <Pagination
                            defaultCurrent={page}
                            defaultPageSize={pageSize}
                            total={totalCount}
                            showSizeChanger
                            locale={{ items_per_page: "개씩 보기" }}
                            current={page}
                            pageSize={pageSize}
                            onShowSizeChange={handlePageSizeChange}
                            onChange={setPage}
                        />
                    </PageNationWrap>
                </TableBottomWrap>
            </ContentBoxWithHeader>
        </ContentsWrap>
    );
};

export default AppVersionManageMent;

const ContentsWrap = styled.div`
    background-color: ${colors.GRAY11};
    height: 100%;
    padding: 40px;
    overflow: auto;
`;

const MainHeaderTitle = styled.h1`
    ${typo.DISPLAY_3}
    color: ${colors.GRAY2};
    margin-bottom: 24px;
`;

const ContentBoxWithHeader = styled.div`
    margin-top: 20px;
    padding: 22px 20px;
    background: ${colors.WHITE};
    border-radius: 16px;
    border: 1px solid ${colors.GRAY9};
`;

const TableHeader = styled.div`
    display: flex;
    justify-content: space-between;
`;

const HeaderButtonWrap = styled.div`
    display: flex;
    max-height: 32px;
    gap: 8px;
`;

const TableTitle = styled.p`
    color: ${colors.GRAY2};
    ${typo.HEADING_4};
    margin-bottom: 18px;
`;

const TableBottomWrap = styled.div`
    display: flex;

    align-items: center;
    justify-content: space-between;
    margin-top: 16px;
`;

const ContentsTotal = styled.span`
    display: flex;
    gap: 8px;
    color: ${colors.GRAY6};
    font-weight: 500;
    ${typo.BODY_8};

    span {
        ${typo.BODY_8};
        font-weight: 500;
        color: ${colors.GRAY2};
    }
`;

const PageNationWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;
